/**
 * WO detail header card — the work-order identity (never a task name).
 * Left: plate · vehicle (title) · WO number. No done counter: the service
 * rows say what is done (user call 2026-09-22, "redundant").
 * Right, its own column at `lg`: the job's time budget — agreed, spent,
 * what is left, and the bar (user call 2026-09-22: "move the estimation,
 * worked and the time counter and the line to the right side as a sub
 * column"). Below `lg` the budget sits under the identity.
 *
 * No customer name or phone here (2026-09-22): the technician surface
 * carries car details only, and the backend no longer sends the contact
 * pair on /detail. No owner line and no "yours" figure either (same day):
 * the budget counts every mechanic's time against the estimate, and whose
 * job it is shows on each service row. The mileage figure and its Update
 * button went too: the technician dashboard shows the car and the job,
 * not a mileage capture — that stays with intake and completion.
 *
 * The single-service name chip that used to sit in row 1 is gone: every
 * service now renders its own header (owner, approval, parts, claim), so the
 * chip printed the same name twice, a few pixels apart.
 */
import { Plate, TimeBudget } from '@vollgas/shared/ui'
import { compactPlate } from '@vollgas/shared/utils'
import type { WorkOrderDetail } from '@/apis/types'
import { useI18n } from '@/i18n/TranslationContext'
import { timerState, totalMinutes } from './woDetail'

interface WoHeaderCardProps {
  readonly wo: WorkOrderDetail
  readonly now: number
}

export function WoHeaderCard({ wo, now }: WoHeaderCardProps) {
  const { t } = useI18n()
  // A car with no make/model/year on file has the backend fall back
  // `vehicleDisplayName` to the raw plate, which printed next to the
  // formatted `<Plate>` badge as the same plate twice ("T-76672 T76672").
  // Compare compact (strip separators, upper-case) so a raw vs. formatted
  // spelling of the same plate is still caught.
  const displayIsPlate =
    !!wo.vehicleDisplayName && compactPlate(wo.vehicleDisplayName) === compactPlate(wo.vehiclePlate ?? '')

  return (
    <div className="rounded-md border border-glass-border bg-glass-fill p-px18 text-glass-text">
      <div className="flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-6">
        <div className="min-w-0 flex-1">
          <div className="flex min-w-0 flex-wrap items-center gap-2.5">
            <Plate text={wo.vehiclePlate} />
            {!displayIsPlate && (
              <span className="truncate font-display text-18 font-extrabold tracking-mk-neg2">{wo.vehicleDisplayName}</span>
            )}
            <span className="text-12-5 font-medium text-glass-text-muted">{wo.reference}</span>
          </div>
        </div>

        {/* Agreed, spent, and what is left of it — the subtraction this
            header used to leave to the reader. Every mechanic's time counts
            against the estimate: a job two mechanics share ran over long
            before either one's own clock said so. */}
        <TimeBudget
          className="lg:w-[44%] lg:shrink-0"
          agreedMinutes={wo.estimateMinutes}
          workedMinutes={totalMinutes(wo.services, now)}
          state={timerState(wo.services)}
          labels={t.tasks.budget}
          size="md"
        />
      </div>
    </div>
  )
}
